import React from 'react';
import ImageUploadBox from '@/components/common/ImageUploadBox';

/** 格式化文件大小 */
function formatFileSize(bytes?: number) {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export interface JobResumeDraftModuleProps {
  loading: boolean;
  submitting: boolean;
  expectedMonthlySalary: string;
  setExpectedMonthlySalary: (val: string) => void;
  resumeAssetId: string;
  setResumeAssetId: (val: string) => void;
  resumeFileObj: Api.Resume.ResumePortfolioAssetResponse | null;
  setResumeFileObj: (val: Api.Resume.ResumePortfolioAssetResponse | null) => void;
  handleSubmitDraft: (e: React.FormEvent) => void;
}

export const JobResumeDraftModule: React.FC<JobResumeDraftModuleProps> = ({
  loading,
  submitting,
  expectedMonthlySalary,
  setExpectedMonthlySalary,
  resumeAssetId,
  setResumeAssetId,
  resumeFileObj,
  setResumeFileObj,
  handleSubmitDraft,
}) => {
  if (loading) {
    return (
      <div className="glass-card rounded-2xl p-12 text-center">
        <div className="inline-block w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-sm text-foreground-500">正在加载求职资料数据...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmitDraft} className="space-y-6">
      {/* 期望薪资 */}
      <div className="glass-card rounded-2xl p-6 md:p-8 space-y-4">
        <h2 className="font-heading font-bold text-base text-foreground-950 flex items-center gap-2 border-b border-gray-100 pb-3">
          <i className="ri-money-cny-box-line text-primary-500 text-lg"></i>
          求职期望薪资
        </h2>

        <div>
          <label className="block text-xs font-medium text-foreground-700 mb-1.5">
            期望月薪 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={expectedMonthlySalary}
            onChange={(e) => setExpectedMonthlySalary(e.target.value)}
            placeholder="如：15000-25000元/月 或 面议"
            className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-white text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
          <p className="text-xs text-foreground-400 mt-1">
            填写您期望的月薪范围，招聘企业审核投递申请时将直接审阅此信息。
          </p>
        </div>
      </div>

      {/* 简历文件附件 */}
      <div className="glass-card rounded-2xl p-6 md:p-8 space-y-4">
        <h2 className="font-heading font-bold text-base text-foreground-950 flex items-center gap-2 border-b border-gray-100 pb-3">
          <i className="ri-attachment-line text-primary-500 text-lg"></i>
          简历文件附件 <span className="text-red-500">*</span>
        </h2>

        <ImageUploadBox
          label="个人简历文件"
          tip="支持 PDF, DOC, DOCX, PNG, JPG 格式"
          scene="PROFILE"
          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
          placeholder="点击或拖拽上传个人简历文件"
          subPlaceholder="上传简历附件便于招聘方直接审阅"
          value={resumeFileObj?.access_url || resumeAssetId}
          fileName={resumeFileObj?.file_name}
          onChange={(assetId, file) => {
            setResumeAssetId(assetId);
            if (file) {
              setResumeFileObj({
                id: assetId,
                file_name: file.name,
                file_size: file.size,
                mime_type: file.type,
                access_url: URL.createObjectURL(file),
              });
            } else if (!assetId) {
              setResumeFileObj(null);
            }
          }}
        />
      </div>

      {/* 提交控制 */}
      <div className="flex gap-4 pt-2">
        {(() => {
          const isSubmitDisabled = submitting || !expectedMonthlySalary.trim() || !(resumeAssetId || resumeFileObj);
          return (
            <button
              type="submit"
              disabled={isSubmitDisabled}
              className={`px-8 py-3.5 rounded-xl font-semibold text-sm transition-all shadow-sm flex items-center gap-2 ${
                isSubmitDisabled
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-primary-500 text-white hover:brightness-95 active:brightness-90 cursor-pointer'
              }`}
            >
              {submitting && <i className="ri-loader-4-line animate-spin"></i>}
              {submitting ? '正在保存...' : '保存求职资料草稿'}
            </button>
          );
        })()}
      </div>
    </form>
  );
};

export default JobResumeDraftModule;
